import { useState, useEffect, useRef } from 'react';
import { getShiftDetails, getShiftDetailsByAdmin, type ShiftDetails, type CloseShiftData, closeShift, closeShiftByAdmin } from '../../../api/shifts.api';
import { generateShiftClosePDF, generateShiftCloseTicket } from '../../../utils/pdfGenerator';
import { validateAdmin } from '../../../api/auth.api';
import { CurrencyInput } from '../../../utils/currencyFormat';

interface ShiftClosureWizardProps {
    onClose: () => void;
    onSuccess: () => void;
    shiftId?: string; // Opcional: cuando admin cierra turno de otro usuario
}

const STEPS = ['Resumen', 'Declaración', 'Conciliación', 'Confirmación', 'Finalizado'];

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount);
};

export default function ShiftClosureWizard({ onClose, onSuccess, shiftId }: ShiftClosureWizardProps) {
    const [step, setStep] = useState(0);
    const [details, setDetails] = useState<ShiftDetails | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [shiftAuthRule, setShiftAuthRule] = useState<'SIEMPRE' | 'SOLO_CIERRE' | 'SOLO_ABRIR' | 'SOLO_DESCUADRE' | 'NUNCA'>('SOLO_DESCUADRE');

    // Form State
    const [efectivoDeclarado, setEfectivoDeclarado] = useState(0);
    const [tarjetaDeclarada, setTarjetaDeclarada] = useState(0);
    // const [qrDeclarado, setQrDeclarado] = useState(0); // Removed as per request
    const [transferenciaDeclarada, setTransferenciaDeclarada] = useState(0);
    const [observaciones, setObservaciones] = useState('');


    // Admin Auth State
    const [showAdminAuth, setShowAdminAuth] = useState(false);
    const [adminEmail, setAdminEmail] = useState('');
    const [adminPassword, setAdminPassword] = useState('');
    const [authError, setAuthError] = useState('');
    const [authorizingAdmin, setAuthorizingAdmin] = useState<string | null>(null);
    const [isAuthorizing, setIsAuthorizing] = useState(false);

    // onClose suele llegar como función inline y cambia en cada render del padre; si fuera
    // dependencia del efecto, los detalles se recargarían en mitad del cierre y, ya cerrado
    // el turno, saldría "No se encontraron detalles del turno" sobre un cierre exitoso.
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        // Fetch details AND settings in parallel
        const detailsPromise = shiftId 
            ? getShiftDetailsByAdmin(shiftId) 
            : getShiftDetails();
            
        Promise.all([
            detailsPromise,
            // Assuming we have an axios instance imported in shifts.api or similar we can use to fetch settings
            // Or we can import axiosInstance from api/axios
            import('../../../api/axios').then(mod => mod.default.get('/companies/settings'))
        ])
        .then(([detailsData, settingsRes]) => {
            if (!detailsData) {
                throw new Error('No se encontraron detalles del turno');
            }
            setDetails(detailsData);
            if (settingsRes.data && settingsRes.data.shiftAuthRule) {
                setShiftAuthRule(settingsRes.data.shiftAuthRule);
            }
        })
        .catch(err => {
            console.error('Error cargando detalles del turno:', err);
            alert(`Error cargando información: ${err.response?.data?.message || err.message || 'No se pudieron obtener los detalles del turno actual.'}`);
            onCloseRef.current(); // Cerrar el modal si hay error
        })
        .finally(() => setIsLoading(false));
    }, [shiftId]);
    
    // Calculations for Difference
    // Safe number parsing helper
    const safeNumber = (val: any) => {
        const num = Number(val);
        return isNaN(num) ? 0 : num;
    };

    // Calculations for Difference
    // We use safeNumber to ensure we always have valid numbers for calculation
    const expectedCash = details ? (safeNumber(details.montoInicial) + safeNumber(details.totalEfectivo) - safeNumber(details.totalGastos)) : 0;
    const diffEfectivo = details ? safeNumber(efectivoDeclarado) - expectedCash : 0;
    const diffTarjeta = details ? safeNumber(tarjetaDeclarada) - safeNumber(details.totalTarjeta) : 0;
    // qrDeclarado is now 0 effectively, or we just remove it from calculation if we want transferencias to cover both
    // Logic: User enters total transferencias (including nequi) in methods. So compared against sum of system values.
    const diffOtros = details ? safeNumber(transferenciaDeclarada) - (safeNumber(details.totalQr) + safeNumber(details.totalTransferencia)) : 0;
    
    // Strict Discrepancy: Check if ANY individual difference exists
    // NaN handling: If calculation somehow resulted in NaN (shouldn't with safeNumber), treat it as discrepancy just in case.
    const hasDiscrepancy = isNaN(diffEfectivo) || isNaN(diffTarjeta) || isNaN(diffOtros) || 
                          Math.abs(diffEfectivo) > 1 || Math.abs(diffTarjeta) > 1 || Math.abs(diffOtros) > 1;
    
    const totalDifference = diffEfectivo + diffTarjeta + diffOtros;

    const nextStep = () => {
        if (step === 2 && hasDiscrepancy && !observaciones.trim()) {
            alert('Es obligatorio ingresar una justificación para la discrepancia.');
            return;
        }
        setStep(prev => prev + 1);
    };

    const prevStep = () => setStep(prev => prev - 1);

    const handleAdminAuth = async (e: React.FormEvent) => {
        e.preventDefault();
        setAuthError('');
        setIsAuthorizing(true);
        try {
            const response = await validateAdmin({ email: adminEmail, password: adminPassword });
            
            if (response && response.valid) {
                setAuthorizingAdmin(response.user.nombre);
                setShowAdminAuth(false);
                // Reset auth state
                setAdminEmail('');
                setAdminPassword('');
                handleSubmit(response.user.nombre); 
            } else {
                setAuthError(response.message || 'Credenciales inválidas o falta de permisos.');
            }
        } catch (err) {
            console.error('Auth error:', err);
            setAuthError('Error de conexión al validar credenciales.');
        } finally {
            setIsAuthorizing(false);
        }
    };

    const handlePrintReport = () => {
         if (details) {
            const finalSupervisor = authorizingAdmin; 
            const finalObservation = `${observaciones} ${finalSupervisor ? `[Autorizado por: ${finalSupervisor}]` : ''}`.trim();

            const data: CloseShiftData = {
                efectivoContado: efectivoDeclarado,
                tarjetaContada: tarjetaDeclarada,
                qrContado: 0, // Always 0
                transferenciaContada: transferenciaDeclarada,
                observaciones: finalObservation
            };
             generateShiftClosePDF(details, data, (details as any).operador, authorizingAdmin);
         }
    };

    const handleSubmit = async (authorizedBy?: string) => {
        // Determine if auth is needed
        const needsAuth = (() => {
            switch (shiftAuthRule) {
                case 'SIEMPRE': 
                case 'SOLO_CIERRE':
                    return true;
                case 'SOLO_DESCUADRE':
                    return hasDiscrepancy;
                case 'NUNCA':
                case 'SOLO_ABRIR':
                    return false;
                default: 
                    return hasDiscrepancy; // Fallback to safe default
            }
        })();

        // If auth needed and not yet authorized
        if (needsAuth && !authorizingAdmin && !authorizedBy) {
            setShowAdminAuth(true);
            return;
        }

        setIsSubmitting(true);
        try {
            const finalSupervisor = authorizedBy || authorizingAdmin;
            const finalObservation = `${observaciones} ${finalSupervisor ? `[Autorizado por: ${finalSupervisor}]` : ''}`.trim();

            const data: CloseShiftData = {
                efectivoContado: efectivoDeclarado,
                tarjetaContada: tarjetaDeclarada,
                qrContado: 0,
                transferenciaContada: transferenciaDeclarada,
                observaciones: finalObservation
            };
            
            // Usar closeShiftByAdmin si hay shiftId (admin cerrando turno de otro)
            if (shiftId) {
                await closeShiftByAdmin(shiftId, data);
            } else {
                await closeShift(data);
            }
            
            // Auto-download PDF on success
            if (details) {
                handlePrintReport();
            }
            
            // Move to Success Step
            setStep(4);
            
        } catch (error) {
            console.error('Error closing shift:', error);
            alert('Error cerrando turno');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePrintTicket = (action: 'print' | 'download') => {
         if (details) {
            const finalSupervisor = authorizingAdmin;
            const finalObservation = `${observaciones} ${finalSupervisor ? `[Autorizado por: ${finalSupervisor}]` : ''}`.trim();

            const data: CloseShiftData = {
                efectivoContado: efectivoDeclarado,
                tarjetaContada: tarjetaDeclarada,
                qrContado: 0,
                transferenciaContada: transferenciaDeclarada,
                observaciones: finalObservation
            };
             
            generateShiftCloseTicket(
                details, 
                data, 
                (details as any).operador,
                { print: action === 'print', download: action === 'download' },
                authorizingAdmin
            );
         }
    };

    const renderStepContent = () => {
        switch (step) {
            case 0: // Resumen Sistema
                return (
                    <div className="space-y-4">
                        <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                            <h3 className="text-sm font-bold text-blue-800 mb-3 uppercase tracking-wider">Totales Esperados (Sistema)</h3>
                            <div className="space-y-2">
                                <div className="flex justify-between text-slate-700">
                                    <span>Efectivo Bruto (Base + Ventas)</span>
                                    <span className="font-bold">{formatCurrency(details!.montoInicial + details!.totalEfectivo)}</span>
                                </div>
                                <div className="flex justify-between text-slate-700">
                                    <span>Tarjetas</span>
                                    <span className="font-bold">{formatCurrency(details!.totalTarjeta)}</span>
                                </div>
                                <div className="flex justify-between text-slate-700">
                                    <span>Transferencias</span>
                                    <span className="font-bold">{formatCurrency(details!.totalQr + details!.totalTransferencia)}</span>
                                </div>
                                {(details!.totalTienda ?? 0) > 0 && (
                                    <div className="flex justify-between text-purple-700 text-sm">
                                        <span>Incluye ventas de tienda</span>
                                        <span className="font-bold">{formatCurrency(details!.totalTienda ?? 0)}</span>
                                    </div>
                                )}
                                {details!.totalGastos > 0 && (
                                    <div className="flex justify-between text-red-600">
                                        <span>Gastos (Efectivo)</span>
                                        <span className="font-bold">-{formatCurrency(details!.totalGastos)}</span>
                                    </div>
                                )}
                                <div className="pt-2 border-t border-blue-200 flex justify-between text-blue-900 font-black text-lg">
                                    <span>Total General</span>
                                    <span>{formatCurrency(details!.totalEsperado + details!.totalTarjeta + details!.totalQr + details!.totalTransferencia)}</span>
                                </div>
                            </div>
                        </div>
                        <p className="text-sm text-slate-500 text-center">
                            Verifique que ha procesado todas las transacciones antes de continuar.
                        </p>
                    </div>
                );

            case 1: // Declaración
                return (
                    <div className="space-y-4">
                        <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-100 mb-2">
                            <p className="text-xs text-yellow-800 flex items-center gap-2">
                                <i className="ri-alert-line"></i>
                                Ingrese los valores reales contados en caja y datáfonos.
                            </p>
                        </div>
                        
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-1">Efectivo en Caja</label>
                            <CurrencyInput
                                value={efectivoDeclarado}
                                onChange={setEfectivoDeclarado}
                                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-xl font-bold text-slate-800 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all"
                                placeholder="$0"
                                autoFocus
                            />
                            <p className="text-xs text-slate-400 mt-1">Incluya la base inicial</p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Total Tarjetas</label>
                                <CurrencyInput
                                    value={tarjetaDeclarada}
                                    onChange={setTarjetaDeclarada}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg font-medium outline-none focus:border-teal-500"
                                    placeholder="$0"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Transferencias</label>
                                <CurrencyInput
                                    value={transferenciaDeclarada}
                                    onChange={setTransferenciaDeclarada}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg font-medium outline-none focus:border-teal-500"
                                    placeholder="$0"
                                />
                            </div>

                        </div>
                    </div>
                );

            case 2: // Conciliación
                return (
                    <div className="space-y-4">
                        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-200">
                                    <tr>
                                        <th className="p-3 text-left">Concepto</th>
                                        <th className="p-3 text-right">Esperado</th>
                                        <th className="p-3 text-right">Declarado</th>
                                        <th className="p-3 text-right">Diferencia</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    <tr>
                                        <td className="p-3 font-medium text-slate-700">
                                            Efectivo
                                            {details!.totalGastos > 0 && <span className="block text-xs text-red-500 font-normal">(Menos gastos: {formatCurrency(details!.totalGastos)})</span>}
                                        </td>
                                        <td className="p-3 text-right text-slate-500">{formatCurrency(details!.montoInicial + details!.totalEfectivo - details!.totalGastos)}</td>
                                        <td className="p-3 text-right font-bold">{formatCurrency(Number(efectivoDeclarado))}</td>
                                        <td className={`p-3 text-right font-bold ${Math.abs(diffEfectivo) > 100 ? (diffEfectivo < 0 ? 'text-red-500' : 'text-blue-500') : 'text-green-500'}`}>
                                            {formatCurrency(diffEfectivo)}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="p-3 font-medium text-slate-700">Digitales</td>
                                        <td className="p-3 text-right text-slate-500">{formatCurrency(details!.totalTarjeta + details!.totalQr + details!.totalTransferencia)}</td>
                                        <td className="p-3 text-right font-bold">{formatCurrency(Number(tarjetaDeclarada) + Number(transferenciaDeclarada))}</td>
                                        <td className={`p-3 text-right font-bold ${Math.abs(diffTarjeta + diffOtros) > 100 ? ((diffTarjeta + diffOtros) < 0 ? 'text-red-500' : 'text-blue-500') : 'text-green-500'}`}>
                                            {formatCurrency(diffTarjeta + diffOtros)}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {hasDiscrepancy ? (
                            <div className="bg-red-50 p-4 rounded-xl border border-red-100 animate-in fade-in slide-in-from-bottom-2">
                                <h4 className="text-red-800 font-bold text-sm mb-2 flex items-center gap-2">
                                    <i className="ri-error-warning-fill"></i>
                                    Diferencias Detectadas
                                </h4>
                                <p className="text-xs text-red-600 mb-3">
                                    {totalDifference === 0 
                                        ? "Los montos individuales no coinciden con lo esperado (posible cruce de medios de pago)."
                                        : <>Existe una diferencia global de <strong>{formatCurrency(totalDifference)}</strong>.</>
                                    }
                                    <br/>Es obligatorio justificar esta discrepancia.
                                </p>
                                <div className="space-y-3">
                                    <textarea
                                        value={observaciones}
                                        onChange={e => setObservaciones(e.target.value)}
                                        className="w-full px-3 py-2 border-2 border-red-200 rounded-lg text-sm outline-none focus:border-red-400 bg-white"
                                        placeholder="Explique el motivo de la diferencia... (Obligatorio)"
                                        rows={2}
                                        required
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="bg-green-50 p-4 rounded-xl border border-green-100 text-center">
                                <p className="text-green-800 font-bold flex items-center justify-center gap-2">
                                    <i className="ri-checkbox-circle-fill text-xl"></i>
                                    ¡Cuadre Perfecto!
                                </p>
                            </div>
                        )}
                    </div>
                );

            case 3: // Confirmación
                return (
                    <div className="space-y-6 text-center py-4">
                        <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto text-teal-600 mb-2">
                            <i className="ri-shield-check-line text-3xl"></i>
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-slate-800 mb-2">Confirmar Cierre de Turno</h3>
                            <p className="text-slate-500 text-sm px-6">
                                Al confirmar, usted declara que la información ingresada es correcta y asume la responsabilidad por los valores reportados.
                            </p>
                        </div>
                        <div className="bg-slate-50 p-4 rounded-xl mx-4 text-left text-sm space-y-2">
                             <div className="flex justify-between">
                                <span className="text-slate-500">Total Declarado:</span>
                                <span className="font-bold text-slate-800">{formatCurrency(Number(efectivoDeclarado) + Number(tarjetaDeclarada) + Number(transferenciaDeclarada))}</span>
                             </div>
                             <div className="flex justify-between">
                                <span className="text-slate-500">Diferencia Neta:</span>
                                <span className={`font-bold ${totalDifference === 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(totalDifference)}</span>
                             </div>
                             {hasDiscrepancy && (
                                <div className="bg-red-50 p-2 rounded text-xs text-red-700 border border-red-100 mt-2">
                                    <i className="ri-lock-2-line mr-1"></i>
                                    {totalDifference === 0 ? 'Inconsistencia en medios de pago.' : 'Diferencia en valores totales.'} Se requerirá autorización.
                                </div>
                             )}
                        </div>
                    </div>
                );
            
            case 4: // Finalizado
                 return (
                    <div className="space-y-6 text-center py-4 animate-in fade-in zoom-in-95 duration-300">
                         <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-200">
                             <i className="ri-check-line text-4xl text-green-600"></i>
                         </div>
                         <div>
                             <h3 className="text-2xl font-bold text-slate-800 mb-2">¡Turno Cerrado Exitosamente!</h3>
                             <p className="text-slate-500 text-sm">
                                 El reporte detallado se ha descargado automáticamente.
                             </p>
                         </div>
                         
                         <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 mx-2">
                             <p className="text-sm font-bold text-slate-700 mb-4 uppercase tracking-wider">Opciones del Ticket</p>
                             <div className="grid grid-cols-2 gap-4">
                                 <button 
                                     onClick={() => handlePrintTicket('print')}
                                     className="flex flex-col items-center justify-center p-4 bg-white border-2 border-slate-100 hover:border-blue-500 hover:bg-blue-50 rounded-xl transition group"
                                     title="Abrir vista de impresión"
                                 >
                                     <i className="ri-printer-line text-3xl text-slate-700 mb-2 group-hover:scale-110 transition-transform"></i>
                                     <span className="font-bold text-slate-700 text-sm">Imprimir</span>
                                 </button>
                                 
                                 <button 
                                     onClick={() => handlePrintTicket('download')}
                                     className="flex flex-col items-center justify-center p-4 bg-white border-2 border-slate-100 hover:border-blue-500 hover:bg-blue-50 rounded-xl transition group"
                                     title="Guardar archivo PDF"
                                 >
                                     <i className="ri-download-line text-3xl text-slate-700 mb-2 group-hover:scale-110 transition-transform"></i>
                                     <span className="font-bold text-slate-700 text-sm">Descargar</span>
                                 </button>
                             </div>
                         </div>
                    </div>
                );
        }
    };

    if (isLoading) {
        return (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-fadeIn">
                <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl flex flex-col animate-scaleIn">
                    {/* Header */}
                    <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                        <h2 className="text-xl font-bold text-slate-800">Cierre de Turno</h2>
                        <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                            <i className="ri-close-line text-2xl"></i>
                        </button>
                    </div>
                    
                    {/* Loading Content */}
                    <div className="flex flex-col items-center justify-center p-16 space-y-6">
                        <div className="relative">
                            <div className="w-20 h-20 rounded-full bg-teal-100 flex items-center justify-center animate-pulse">
                                <i className="ri-time-line text-4xl text-teal-600"></i>
                            </div>
                            <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-white shadow-lg flex items-center justify-center">
                                <i className="ri-loader-4-line text-lg text-teal-600 animate-spin"></i>
                            </div>
                        </div>
                        <div className="text-center">
                            <p className="text-lg font-bold text-slate-800 mb-1">Preparando Cierre</p>
                            <p className="text-slate-500 text-sm">Calculando totales y movimientos del turno...</p>
                        </div>
                        <div className="flex gap-2">
                            <div className="w-2 h-2 rounded-full bg-teal-500 animate-bounce" style={{ animationDelay: '0ms' }}></div>
                            <div className="w-2 h-2 rounded-full bg-teal-500 animate-bounce" style={{ animationDelay: '150ms' }}></div>
                            <div className="w-2 h-2 rounded-full bg-teal-500 animate-bounce" style={{ animationDelay: '300ms' }}></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!details) {
        return (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
                <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-8 text-center">
                    <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                        <i className="ri-error-warning-line text-3xl text-red-500"></i>
                    </div>
                    <p className="text-red-600 font-bold mb-2">Error cargando información</p>
                    <p className="text-slate-500 text-sm mb-6">No se pudieron obtener los detalles del turno actual.</p>
                    <button 
                        onClick={onClose} 
                        className="px-6 py-2 bg-slate-800 text-white rounded-xl font-medium hover:bg-slate-900 transition"
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-slate-800">Cierre de Turno {details?.numero}</h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                        <i className="ri-close-line text-2xl"></i>
                    </button>
                </div>

                {/* Progress Bar */}
                <div className="px-6 pt-6 mb-2">
                    <div className="flex items-center justify-between mb-2">
                        {STEPS.map((s, idx) => (
                            <div key={idx} className={`text-xs font-bold ${idx <= step ? 'text-teal-600' : 'text-slate-300'}`}>
                                {s}
                            </div>
                        ))}
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden flex">
                        {STEPS.map((_, idx) => (
                            <div 
                                key={idx} 
                                className={`flex-1 h-full transition-all duration-300 ${idx <= step ? 'bg-teal-500' : 'bg-transparent'} ${idx < step ? 'border-r border-white/20' : ''}`}
                            ></div>
                        ))}
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 flex-1 overflow-y-auto">
                    {renderStepContent()}
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-slate-100 bg-slate-50 rounded-b-2xl flex gap-3">
                    {step === 4 ? (
                         <button 
                            onClick={onSuccess}
                            className="w-full bg-slate-800 text-white py-3 rounded-xl font-bold hover:bg-slate-900 transition-all shadow-lg"
                        >
                            Finalizar y Salir
                        </button>
                    ) : (
                        <>
                            {step > 0 && (
                                <button 
                                    onClick={prevStep}
                                    disabled={isSubmitting}
                                    className="px-6 py-3 border border-slate-300 rounded-xl font-bold text-slate-600 hover:bg-white transition-colors"
                                >
                                    Atrás
                                </button>
                            )}
                            
                            {step < 3 ? ( 
                                <button 
                                    onClick={nextStep}
                                    disabled={step === 2 && hasDiscrepancy && !observaciones.trim()}
                                    className="flex-1 bg-slate-900 text-white py-3 rounded-xl font-bold hover:bg-slate-800 transition-colors shadow-lg shadow-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Siguiente
                                </button>
                            ) : (
                                <button 
                                    onClick={() => handleSubmit()}
                                    disabled={isSubmitting}
                                    className="flex-1 bg-gradient-to-r from-teal-600 to-teal-500 text-white py-3 rounded-xl font-bold hover:from-teal-700 hover:to-teal-600 transition-all shadow-lg shadow-teal-100 flex items-center justify-center gap-2"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <i className="ri-loader-4-line animate-spin"></i>
                                            Procesando Cierre...
                                        </>
                                    ) : (
                                        <>
                                            {hasDiscrepancy ? <i className="ri-lock-2-line"></i> : <i className="ri-check-double-line"></i>}
                                            {hasDiscrepancy ? 'Autorizar y Cerrar' : 'Confirmar Cierre'}
                                        </>
                                    )}
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Admin Auth Modal */}
            {showAdminAuth && (
                 <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[70] p-4 animate-fadeIn">
                    <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-6 animate-scaleIn">
                        <div className="text-center mb-6">
                            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto text-red-600 mb-3">
                                <i className="ri-admin-line text-3xl"></i>
                            </div>
                            <h3 className="text-lg font-bold text-slate-800">Autorización Requerida</h3>
                            <p className="text-sm text-slate-500">
                                Se requiere credenciales de administrador para cerrar turno con diferencias.
                            </p>
                        </div>

                        <form onSubmit={handleAdminAuth} className="space-y-4">
                            {authError && (
                                <div className="bg-red-50 text-red-600 text-xs p-3 rounded-lg border border-red-100">
                                    {authError}
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Email Administrador</label>
                                <input
                                    type="email"
                                    required
                                    value={adminEmail}
                                    onChange={e => setAdminEmail(e.target.value)}
                                    disabled={isAuthorizing}
                                    className="w-full px-4 py-2 border border-slate-200 rounded-lg outline-none focus:border-teal-500 disabled:bg-slate-50"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Contraseña</label>
                                <input
                                    type="password"
                                    required
                                    value={adminPassword}
                                    onChange={e => setAdminPassword(e.target.value)}
                                    disabled={isAuthorizing}
                                    className="w-full px-4 py-2 border border-slate-200 rounded-lg outline-none focus:border-teal-500 disabled:bg-slate-50"
                                />
                            </div>
                            <div className="flex gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowAdminAuth(false)}
                                    disabled={isAuthorizing}
                                    className="px-4 py-2 rounded-lg font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-50"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={isAuthorizing}
                                    className="flex-1 bg-red-600 text-white py-2 rounded-lg font-bold hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {isAuthorizing ? (
                                        <>
                                            <i className="ri-loader-4-line animate-spin"></i>
                                            Autorizando...
                                        </>
                                    ) : (
                                        'Autorizar'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                 </div>
            )}
        </div>
    );
}
